// S3 outputs panel (§2.9): co-products with a live apportionment SHARE METER
// (Σ must stay under 100 %, the remainder shown on the primary), and
// by-products with NRV inputs. NRV is a cost signal (§7): its editing surface
// renders ONLY when the field is present on the loaded outputs (`costVisible`)
// — a no-cost role never sees or fabricates an NRV value.
//
// UX-2: the split used to be a six-colour SVG donut. The register has exactly
// one accent and no categorical palette — "color is semantic, only" (§1 rule
// 3) — so the split is now the §3.4 meter grammar: one stacked bar on
// `--meter-track`, each co-product a step-down TINT of the one accent, the
// whole bar turning `--bad` when Σ ≥ 100 because that is the one thing here
// that is actually wrong. Same numbers, same testids, same live behaviour,
// and it reads at a glance on a phone where a 128 px donut did not.

import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { ProductSelect, UomSelect } from "./pickers";
import {
  computeApportionment,
  freshKey,
  type ApportionmentResult,
  type EditableOutput,
} from "./helpers";

type Product = components["schemas"]["Product"];
type Uom = components["schemas"]["Uom"];

/** Opacity steps for the accent tint of successive co-product shares. */
const SHARE_TINTS = [1, 0.78, 0.6, 0.46, 0.35, 0.27];

export function OutputsPanel({
  outputs,
  products,
  uoms,
  readOnly,
  costVisible,
  defaultCurrency,
  onChange,
}: {
  outputs: readonly EditableOutput[];
  products: readonly Product[];
  uoms: readonly Uom[];
  readOnly: boolean;
  costVisible: boolean;
  defaultCurrency: string;
  onChange: (outputs: EditableOutput[]) => void;
}) {
  const { t } = useTranslation();

  const coProducts = outputs.filter((o) => o.output_kind === "co_product");
  const byProducts = outputs.filter((o) => o.output_kind === "by_product");
  const apportionment = computeApportionment(coProducts);
  const firstUom = uoms[0]?.id ?? "";

  const replace = (key: string, fields: Partial<EditableOutput>) => {
    onChange(outputs.map((o) => (o.key === key ? { ...o, ...fields } : o)));
  };
  const remove = (key: string) => {
    onChange(outputs.filter((o) => o.key !== key));
  };
  const addCoProduct = () => {
    const next: EditableOutput = {
      key: freshKey("output"),
      id: undefined,
      output_kind: "co_product",
      product_id: "",
      qty: 1,
      uom_id: firstUom,
      apportionment_pct: 10,
      nrv_per_price_unit_minor: undefined,
      nrv_price_unit_qty: null,
      currency: null,
    };
    onChange([...outputs, next]);
  };
  const addByProduct = () => {
    const next: EditableOutput = {
      key: freshKey("output"),
      id: undefined,
      output_kind: "by_product",
      product_id: "",
      qty: 1,
      uom_id: firstUom,
      apportionment_pct: null,
      nrv_per_price_unit_minor: null,
      nrv_price_unit_qty: 1,
      currency: defaultCurrency,
    };
    onChange([...outputs, next]);
  };

  return (
    <div className="bom-outputs" data-testid="outputs-panel">
      <section className="bom-outputs-section">
        <div className="bom-outputs-head">
          <h3>{t("catalogue.bomEditor.outputs.coProducts")}</h3>
          {readOnly ? null : (
            <button type="button" className="ds-lnk" onClick={addCoProduct}>
              {t("catalogue.bomEditor.outputs.addCoProduct")}
            </button>
          )}
        </div>

        <ApportionmentMeter apportionment={apportionment} />

        {coProducts.length === 0 ? null : (
          <ul className="bom-output-list">
            {coProducts.map((output, index) => (
              <li className="bom-output-row" key={output.key} data-testid="co-product-row">
                <span
                  className="bom-output-swatch"
                  style={{ opacity: shareTint(index) }}
                  aria-hidden="true"
                />
                <ProductSelect
                  products={products}
                  value={output.product_id}
                  disabled={readOnly}
                  placeholder={t("catalogue.bomEditor.outputs.productPlaceholder")}
                  onChange={(product_id) => {
                    replace(output.key, { product_id });
                  }}
                />
                <label className="bom-inline-field">
                  {t("catalogue.bomEditor.outputs.qty")}
                  <input
                    type="number"
                    min={0}
                    step="any"
                    disabled={readOnly}
                    value={Number.isNaN(output.qty) ? "" : output.qty}
                    onChange={(e) => {
                      replace(output.key, {
                        qty: e.target.value === "" ? Number.NaN : Number(e.target.value),
                      });
                    }}
                  />
                </label>
                <UomSelect
                  uoms={uoms}
                  value={output.uom_id}
                  disabled={readOnly}
                  onChange={(uom_id) => {
                    replace(output.key, { uom_id });
                  }}
                />
                <label className="bom-inline-field">
                  {t("catalogue.bomEditor.outputs.apportionmentPct")}
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="any"
                    disabled={readOnly}
                    data-testid="apportionment-input"
                    value={output.apportionment_pct ?? ""}
                    onChange={(e) => {
                      replace(output.key, {
                        apportionment_pct: e.target.value === "" ? null : Number(e.target.value),
                      });
                    }}
                  />
                </label>
                {readOnly ? null : (
                  <button
                    type="button"
                    className="ds-lnk ds-lnk-danger"
                    onClick={() => {
                      remove(output.key);
                    }}
                  >
                    {t("catalogue.bomEditor.outputs.removeOutput")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bom-outputs-section">
        <div className="bom-outputs-head">
          <h3>{t("catalogue.bomEditor.outputs.byProducts")}</h3>
          {readOnly ? null : costVisible ? (
            <button type="button" className="ds-lnk" onClick={addByProduct}>
              {t("catalogue.bomEditor.outputs.addByProduct")}
            </button>
          ) : (
            <span className="bom-hint">{t("catalogue.bomEditor.outputs.addByProductNoCost")}</span>
          )}
        </div>

        {byProducts.length === 0 && coProducts.length === 0 ? (
          <p className="bom-hint">{t("catalogue.bomEditor.outputs.none")}</p>
        ) : null}

        {byProducts.length > 0 ? (
          <ul className="bom-output-list">
            {byProducts.map((output) => (
              <li className="bom-output-row" key={output.key} data-testid="by-product-row">
                <ProductSelect
                  products={products}
                  value={output.product_id}
                  disabled={readOnly}
                  placeholder={t("catalogue.bomEditor.outputs.productPlaceholder")}
                  onChange={(product_id) => {
                    replace(output.key, { product_id });
                  }}
                />
                <label className="bom-inline-field">
                  {t("catalogue.bomEditor.outputs.qty")}
                  <input
                    type="number"
                    min={0}
                    step="any"
                    disabled={readOnly}
                    value={Number.isNaN(output.qty) ? "" : output.qty}
                    onChange={(e) => {
                      replace(output.key, {
                        qty: e.target.value === "" ? Number.NaN : Number(e.target.value),
                      });
                    }}
                  />
                </label>
                <UomSelect
                  uoms={uoms}
                  value={output.uom_id}
                  disabled={readOnly}
                  onChange={(uom_id) => {
                    replace(output.key, { uom_id });
                  }}
                />
                {output.nrv_per_price_unit_minor === undefined ? (
                  <span className="bom-hint bom-nrv-redacted" data-testid="nrv-redacted">
                    {t("catalogue.bomEditor.outputs.nrvRedacted")}
                  </span>
                ) : (
                  <>
                    <label className="bom-inline-field">
                      {t("catalogue.bomEditor.outputs.nrv")}
                      <input
                        type="number"
                        min={0}
                        step={1}
                        disabled={readOnly}
                        data-testid="nrv-input"
                        value={output.nrv_per_price_unit_minor ?? ""}
                        onChange={(e) => {
                          replace(output.key, {
                            nrv_per_price_unit_minor:
                              e.target.value === "" ? null : Number(e.target.value),
                          });
                        }}
                      />
                    </label>
                    <label className="bom-inline-field">
                      {t("catalogue.bomEditor.outputs.nrvPriceUnitQty")}
                      <input
                        type="number"
                        min={0}
                        step="any"
                        disabled={readOnly}
                        value={output.nrv_price_unit_qty ?? ""}
                        onChange={(e) => {
                          replace(output.key, {
                            nrv_price_unit_qty:
                              e.target.value === "" ? null : Number(e.target.value),
                          });
                        }}
                      />
                    </label>
                    <label className="bom-inline-field">
                      {t("catalogue.bomEditor.outputs.currency")}
                      <input
                        type="text"
                        maxLength={3}
                        disabled={readOnly}
                        className="bom-currency-input"
                        value={output.currency ?? ""}
                        onChange={(e) => {
                          replace(output.key, {
                            currency: e.target.value === "" ? null : e.target.value.toUpperCase(),
                          });
                        }}
                      />
                    </label>
                  </>
                )}
                {readOnly ? null : (
                  <button
                    type="button"
                    className="ds-lnk ds-lnk-danger"
                    onClick={() => {
                      remove(output.key);
                    }}
                  >
                    {t("catalogue.bomEditor.outputs.removeOutput")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

function shareTint(index: number): number {
  return SHARE_TINTS[index % SHARE_TINTS.length] ?? 1;
}

function ApportionmentMeter({ apportionment }: { apportionment: ApportionmentResult }) {
  const { t } = useTranslation();
  const { segments, coProductTotal, primaryRemainder, valid } = apportionment;
  const remainderShown = Math.max(0, primaryRemainder);
  return (
    <div className="bom-share" data-testid="apportionment-donut">
      {/* THE BAR IS THE WHOLE BATCH. It used to render ONLY the co-product
       * segments — so a batch whose co-products took 30% drew a bar one-third
       * full, directly above a legend reading "PRIMARY 70%". A meter that
       * disagrees with its own label teaches the reader to distrust it. The
       * primary's remainder is now the FIRST segment, at full accent, and the
       * co-products step down in tint after it; the track is only visible when
       * the set is invalid (Σ > 100), which is exactly when something is
       * missing. */}
      <div
        className="bom-share-bar"
        role="img"
        aria-label={t("catalogue.bomEditor.outputs.shareBarLabel", {
          primary: formatPct(remainderShown),
          total: formatPct(coProductTotal),
        })}
      >
        {valid ? (
          <span
            className="bom-share-seg bom-share-seg-primary"
            data-testid="share-seg-primary"
            style={{ width: `${Math.min(100, Math.max(0, remainderShown))}%` }}
          />
        ) : null}
        {segments.map((seg, index) => (
          <span
            key={seg.key}
            className={`bom-share-seg${valid ? "" : " is-over"}`}
            style={{
              width: `${Math.min(100, Math.max(0, seg.pct))}%`,
              opacity: valid ? shareTint(index) : 1,
            }}
          />
        ))}
      </div>
      <div className="bom-share-legend">
        <span className="bom-share-label">{t("catalogue.bomEditor.outputs.primaryShare")}</span>
        <span className="bom-share-pct" data-testid="primary-remainder">
          {formatPct(remainderShown)}%
        </span>
        <span className="bom-share-label bom-share-label-co">
          {t("catalogue.bomEditor.outputs.coProductShare")}
        </span>
        <span className="bom-share-pct" data-testid="co-product-total">
          {formatPct(coProductTotal)}%
        </span>
      </div>
      <p
        className={`bom-share-status${valid ? "" : " is-invalid"}`}
        data-testid="apportionment-status"
        role={valid ? undefined : "alert"}
      >
        {valid
          ? t("catalogue.bomEditor.outputs.valid", { total: formatPct(coProductTotal) })
          : t("catalogue.bomEditor.outputs.overAllocated", { total: formatPct(coProductTotal) })}
      </p>
    </div>
  );
}

function formatPct(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}
